import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { listImportSheets, type PreviewOutput } from '@/api/imports';
import { usePreviewImport } from '@/hooks/useImports';
import { Button } from './Button';
import { MappingEditor } from './MappingEditor';
import { PreviewRows } from './PreviewRows';
import {
  inspectImportColumns,
  listImportMappings,
  type MappingSuggestion,
  type MappingView,
} from '@/api/mappings';

type Props = {
  importId: number;
  /** Feuille déjà mémorisée sur l'import (dernier aperçu réussi). */
  initialSheet?: string | null;
  /** Remonte l'état au parent : la feuille retenue et si l'aperçu est bon. */
  onPreview?: (result: { sheet: string; preview: PreviewOutput | null }) => void;
  /** Lance un aperçu dès l'affichage. */
  autoPreview?: boolean;
  children?: (preview: PreviewOutput | null, loading: boolean, error: string | null) => React.ReactNode;
};

/**
 * Préparation d'un import : choix de la feuille et aperçu à blanc.
 *
 * Ce bloc est partagé par l'assistant (étape « Mappage et simulation ») et par
 * la fiche d'un import resté en attente. C'est délibéré : reprendre un import
 * depuis l'historique doit être EXACTEMENT le même geste que le préparer juste
 * après l'avoir téléversé. Deux implémentations parallèles auraient divergé au
 * premier correctif, et c'est précisément l'écart qui rendait un import en
 * attente lançable à l'aveugle, sans aperçu ni choix de feuille.
 *
 * L'aperçu ne persiste rien (simulation annulée côté serveur) : on peut donc en
 * relancer autant qu'on veut, et le serveur mémorise la feuille du dernier
 * aperçu réussi — ce qui autorise ensuite le lancement.
 */
export function ImportPrepare({
  importId,
  initialSheet,
  onPreview,
  autoPreview = true,
  children,
}: Props) {
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheet, setSheet] = useState(initialSheet ?? '');
  const [preview, setPreview] = useState<PreviewOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Profil de correspondance : soit reconnu automatiquement à la structure du
  // fichier, soit construit à la main dans l'éditeur.
  const [mappingId, setMappingId] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<MappingSuggestion[]>([]);
  // Profils enregistrés : l'opérateur doit pouvoir en choisir un, pas seulement
  // subir la reconnaissance automatique ou tout ressaisir.
  const [profiles, setProfiles] = useState<MappingView[]>([]);
  const [editing, setEditing] = useState(false);
  const previewMutation = usePreviewImport();

  // Les feuilles sont lues une fois par import ; la sélection retenue est celle
  // déjà mémorisée côté serveur, sinon la première.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const found = await listImportSheets(importId);
      if (cancelled) return;
      setSheets(found);
      const chosen = initialSheet || found[0] || '';
      setSheet(chosen);

      // Le fichier suivant d'un même partenaire a la même structure : si un
      // profil a déjà été bâti dessus, on le propose au lieu de laisser
      // l'opérateur tout ressaisir.
      try {
        const [report, saved] = await Promise.all([
          inspectImportColumns(importId, chosen, 20),
          listImportMappings(),
        ]);
        if (!cancelled) {
          setSuggestions(report.suggestions ?? []);
          setProfiles(saved);
        }
      } catch {
        /* fichier non tabulaire ou illisible : l'aperçu dira pourquoi */
      }

      if (autoPreview) void runPreview(chosen);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId]);

  async function runPreview(target: string, mapping?: number | null) {
    setError(null);
    try {
      const result = await previewMutation.mutateAsync({
        id: importId,
        sheet: target || undefined,
        mappingId: mapping ?? mappingId ?? undefined,
      });
      setPreview(result);
      onPreview?.({ sheet: target, preview: result });
    } catch (e) {
      setPreview(null);
      // Le message du serveur nomme la colonne manquante et les colonnes
      // trouvées : il est bien plus utile que « aperçu impossible ».
      setError(e instanceof Error ? e.message : String(e));
      onPreview?.({ sheet: target, preview: null });
    }
  }

  async function handleSheetChange(next: string) {
    if (next === sheet) return;
    setSheet(next);
    setPreview(null);
    await runPreview(next);
  }

  // L'éditeur prend toute la carte quand il est ouvert : mélanger une grille de
  // correspondance avec le résultat d'un aperçu qui ne la reflète pas encore
  // n'aide personne.
  if (editing) {
    return (
      <div>
        <div className="prepare-step">
          <span className="prepare-step-num">2</span>
          Correspondance des colonnes
        </div>
        <MappingEditor
          importId={importId}
          sheet={sheet}
          onCancel={() => setEditing(false)}
          onSaved={(mapping) => {
            setEditing(false);
            setMappingId(mapping.id);
            void runPreview(sheet, mapping.id);
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Étape 1 — comment lire le fichier : la feuille, puis la façon dont ses
          colonnes deviennent des événements. Les deux répondent à la même
          question et vont donc ensemble. */}
      <div className="prepare-step">
        <span className="prepare-step-num">1</span>
        Comment lire le fichier
      </div>

      <div className="prepare-rows">
        {sheets.length > 1 && (
          <div className="prepare-row">
            <span className="prepare-row-label">Feuille</span>
            <select
              value={sheet}
              onChange={(e) => void handleSheetChange(e.target.value)}
              disabled={previewMutation.isPending}
            >
              {sheets.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span className="muted">{sheets.length} feuilles détectées</span>
          </div>
        )}

        <div className="prepare-row">
          <span className="prepare-row-label">Colonnes</span>
          <select
            value={mappingId ?? ''}
            disabled={previewMutation.isPending}
            onChange={(e) => {
              const next = e.target.value ? Number(e.target.value) : null;
              setMappingId(next);
              void runPreview(sheet, next);
            }}
          >
            <option value="">Lecture automatique par le connecteur de la source</option>
            {profiles.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} (v{m.version})
                {suggestions.some((s) => s.id === m.id) ? ' — correspond à ce fichier' : ''}
              </option>
            ))}
          </select>
          <Button size="sm" icon="filter" onClick={() => setEditing(true)}>
            {mappingId ? 'Modifier' : 'Définir un profil'}
          </Button>
        </div>
      </div>

      {suggestions.length > 0 && mappingId == null && (
        <div className="mapping-suggest">
          <Icon name="check" size={15} />
          <div style={{ flex: 1 }}>
            <strong>{suggestions[0].name}</strong> (v{suggestions[0].version}) a été bâti sur un
            fichier de cette structure.
          </div>
          <Button
            size="sm"
            onClick={() => {
              setMappingId(suggestions[0].id);
              void runPreview(sheet, suggestions[0].id);
            }}
          >
            Appliquer
          </Button>
        </div>
      )}

      {/* Étape 2 — vérifier ce que ça donne, sans rien écrire. */}
      <div className="prepare-step" style={{ marginTop: 18 }}>
        <span className="prepare-step-num">2</span>
        Vérification
        <span className="muted" style={{ fontWeight: 600, fontSize: 12 }}>
          simulation, aucune écriture
        </span>
      </div>

      <div className="prepare-rows">
        <div className="prepare-row">
          <Button
            size="sm"
            icon="play"
            onClick={() => void runPreview(sheet)}
            loading={previewMutation.isPending}
          >
            {previewMutation.isPending ? 'Aperçu…' : preview || error ? 'Relancer l’aperçu' : "Lancer l'aperçu"}
          </Button>
        </div>

        {error && <div className="err-msg">Aperçu impossible : {error}</div>}

        {preview && !previewMutation.isPending && <PreviewRows preview={preview} />}

        {children?.(preview, previewMutation.isPending, error)}
      </div>
    </div>
  );
}
